import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { ApiError } from './lib/api';
import { useDevice } from './lib/useDevice';
import { irA, pathDe, consultaDe, useRuta } from './lib/ruta';
import { textos } from './textos';
import { AvisoSoloEscritorio } from './components/ui/AvisoSoloEscritorio';
import { Btn } from './components/ui/Btn';
import { leerTemaLocal } from './features/branding/tema';
import { MobileShell } from './layout/MobileShell';
import { DesktopShell } from './layout/DesktopShell';
import { PlatformShell } from './layout/PlatformShell';
import { aplicarTemaVisual, leerTemaVisual } from './lib/preferencias';
import {
  cerrarSesion,
  elegirEmpresa,
  entrarLocal,
  loginUrl,
  leerSesion,
  listarEmpresas,
  type EmpresaDeSesion,
  type SesionMe,
} from './features/session/api';

type Fase = 'comprobando' | 'sinSesion' | 'sinEmpresa' | 'soloPlataforma' | 'listo' | 'error';

const PlatformScreen = lazy(async () => {
  const mod = await import('./features/platform/PlatformScreen');
  return { default: mod.PlatformScreen };
});
const MastersDesktop = lazy(async () => {
  const mod = await import('./features/masters/MastersDesktop');
  return { default: mod.MastersDesktop };
});
const QrBatchesDesktop = lazy(async () => {
  const mod = await import('./features/qr/QrBatchesDesktop');
  return { default: mod.QrBatchesDesktop };
});
const AssetsScreen = lazy(async () => {
  const mod = await import('./features/assets/index');
  return { default: mod.AssetsScreen };
});
const AssetDetailScreen = lazy(async () => {
  const mod = await import('./features/assets/AssetDetailScreen');
  return { default: mod.AssetDetailScreen };
});
const AssetFormScreen = lazy(async () => {
  const mod = await import('./features/assets/AssetFormScreen');
  return { default: mod.AssetFormScreen };
});
const ScannerScreen = lazy(async () => {
  const mod = await import('./features/scanner/ScannerScreen');
  return { default: mod.ScannerScreen };
});
const PublicIdentifyScreen = lazy(async () => {
  const mod = await import('./features/scanner/PublicIdentifyScreen');
  return { default: mod.PublicIdentifyScreen };
});
const ScanSessionScreen = lazy(async () => {
  const mod = await import('./features/movements/ScanSessionScreen');
  return { default: mod.ScanSessionScreen };
});
const RentalsScreen = lazy(async () => {
  const mod = await import('./features/rentals/RentalsScreen');
  return { default: mod.RentalsScreen };
});
const DashboardScreen = lazy(async () => {
  const mod = await import('./features/dashboard/DashboardScreen');
  return { default: mod.DashboardScreen };
});
const BrandingScreen = lazy(async () => {
  const mod = await import('./features/branding/BrandingScreen');
  return { default: mod.BrandingScreen };
});
const NotificationsScreen = lazy(async () => {
  const mod = await import('./features/notifications/NotificationsScreen');
  return { default: mod.NotificationsScreen };
});
const ReportsScreen = lazy(async () => {
  const mod = await import('./features/reports/ReportsScreen');
  return { default: mod.ReportsScreen };
});
const MaintenanceScreen = lazy(async () => {
  const mod = await import('./features/maintenance/MaintenanceScreen');
  return { default: mod.MaintenanceScreen };
});
const AuditScreen = lazy(async () => {
  const mod = await import('./features/audit/AuditScreen');
  return { default: mod.AuditScreen };
});
const ImportScreen = lazy(async () => {
  const mod = await import('./features/imports/ImportScreen');
  return { default: mod.ImportScreen };
});
const StorageScreen = lazy(async () => {
  const mod = await import('./features/files/StorageScreen');
  return { default: mod.StorageScreen };
});
const PreferenciasScreen = lazy(async () => {
  const mod = await import('./features/preferencias/PreferenciasScreen');
  return { default: mod.PreferenciasScreen };
});

export function App(): React.JSX.Element {
  const device = useDevice();
  const ruta = useRuta();
  const [fase, setFase] = useState<Fase>('comprobando');
  const [sesion, setSesion] = useState<SesionMe | null>(null);
  const [empresas, setEmpresas] = useState<readonly EmpresaDeSesion[]>([]);
  const [avisoLogin, setAvisoLogin] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setFase('comprobando');
    try {
      const yo = await leerSesion();
      if (!yo) {
        setSesion(null);
        setFase('sinSesion');
        return;
      }
      setSesion(yo);
      if (!yo.company) {
        if (yo.platformAdmin && pathDe(window.location.pathname).startsWith('/plataforma')) {
          setFase('listo');
          return;
        }
        const lista = await listarEmpresas();
        setEmpresas(lista);
        setFase(yo.platformAdmin && lista.length === 0 ? 'soloPlataforma' : 'sinEmpresa');
        return;
      }
      setFase('listo');
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) {
        setFase('error');
        return;
      }
      setFase('error');
    }
  }, []);

  useEffect(() => {
    aplicarTemaVisual(leerTemaVisual());
    void cargar();
  }, [cargar]);

  // Salir del panel sin empresa activa: elegir una membresía, o explicar que la cuenta sólo opera la plataforma.
  useEffect(() => {
    if (!sesion?.platformAdmin || sesion.company) return;
    if (pathDe(ruta).startsWith('/plataforma')) {
      setFase('listo');
      return;
    }
    let cancelado = false;
    void listarEmpresas()
      .then((lista) => {
        if (cancelado) return;
        setEmpresas(lista);
        setFase(lista.length === 0 ? 'soloPlataforma' : 'sinEmpresa');
      })
      .catch(() => {
        if (!cancelado) setFase('error');
      });
    return () => {
      cancelado = true;
    };
  }, [ruta, sesion]);

  if (pathDe(ruta).startsWith('/identificar')) {
    return (
      <Suspense fallback={<p className="p-6 text-brand-text-muted">{textos.sesion.comprobando}</p>}>
        <PublicIdentifyScreen onVolver={() => irA('/')} />
      </Suspense>
    );
  }

  if (fase === 'listo' && sesion?.platformAdmin && pathDe(ruta).startsWith('/plataforma')) {
    return (
      <PlatformShell onCerrar={() => void cerrarSesion(sesion.csrfToken)}>
        <Suspense fallback={<p className="p-4 text-brand-text-muted">{textos.sesion.comprobando}</p>}>
          {device === 'movil' ? <AvisoSoloEscritorio /> : <PlatformScreen sesion={sesion} />}
        </Suspense>
      </PlatformShell>
    );
  }

  if (fase !== 'listo' || !sesion?.company) {
    const tema = leerTemaLocal();
    return (
      <main
        className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-brand-surface p-6"
        translate="no"
      >
        <header className="text-center">
          <h1 className="text-3xl font-semibold text-brand-text">
            {tema?.loginTitle || textos.app.loginTitulo}
          </h1>
          <p className="mt-1 text-brand-text-muted">{tema?.loginSubtitle || textos.app.loginSubtitulo}</p>
        </header>
        {fase === 'comprobando' ? <p className="text-brand-text-muted">{textos.sesion.comprobando}</p> : null}
        {fase === 'sinSesion' && !import.meta.env.DEV ? (
          <>
            <Btn variante="escanear" onClick={() => irA('/identificar')}>
              {textos.acciones.escanear}
            </Btn>
            <Btn variante="primario" onClick={() => window.location.assign(loginUrl())}>
              {textos.acciones.iniciarSesion}
            </Btn>
          </>
        ) : null}
        {fase === 'sinSesion' && import.meta.env.DEV ? (
          <>
            <Btn
              variante="escanear"
              onClick={() => irA('/identificar')}
            >
              {textos.acciones.escanear}
            </Btn>
            <Btn
              variante="primario"
              onClick={() => {
                setAvisoLogin(null);
                void entrarLocal('admin@local')
                  .then(() => void cargar())
                  .catch((error: unknown) => {
                    setAvisoLogin(error instanceof Error ? error.message : textos.sesion.error);
                  });
              }}
            >
              {textos.acciones.entrarLocalAdmin}
            </Btn>
            <Btn
              variante="secundario"
              onClick={() => {
                setAvisoLogin(null);
                void entrarLocal('operario@local')
                  .then(() => void cargar())
                  .catch((error: unknown) => {
                    setAvisoLogin(error instanceof Error ? error.message : textos.sesion.error);
                  });
              }}
            >
              {textos.acciones.entrarLocalOperario}
            </Btn>
            {avisoLogin ? <p className="max-w-md text-center text-brand-danger">{avisoLogin}</p> : null}
          </>
        ) : null}
        {fase === 'sinEmpresa' && sesion ? (
          <SelectorEmpresas
            empresas={empresas}
            csrfToken={sesion.csrfToken}
            onElegida={() => void cargar()}
            onVolverAlPanel={sesion.platformAdmin ? () => irA('/plataforma') : null}
          />
        ) : null}
        {fase === 'soloPlataforma' && sesion ? (
          <section className="flex w-full max-w-md flex-col items-center gap-3 text-center">
            <p className="text-brand-text">{textos.sesion.soloPlataforma(sesion.user.email)}</p>
            <Btn variante="primario" onClick={() => irA('/plataforma')}>
              {textos.sesion.irAlPanel}
            </Btn>
          </section>
        ) : null}
        {fase === 'error' ? (
          <>
            <p className="text-brand-danger">{textos.sesion.error}</p>
            <Btn variante="primario" onClick={() => void cargar()}>
              {textos.acciones.reintentar}
            </Btn>
          </>
        ) : null}
        <footer className="text-sm text-brand-text-muted">{textos.dispositivo[device]}</footer>
      </main>
    );
  }

  const contenido = (
    <Suspense fallback={<p className="p-4 text-brand-text-muted">{textos.sesion.comprobando}</p>}>
      <Rutas ruta={ruta} sesion={sesion} device={device} />
    </Suspense>
  );

  const onCerrar = (): void => {
    void cerrarSesion(sesion.csrfToken);
  };

  if (device !== 'movil' && pathDe(ruta).startsWith('/plataforma') && sesion.platformAdmin) {
    return <PlatformShell onCerrar={onCerrar}>{contenido}</PlatformShell>;
  }

  if (device === 'movil') {
    return (
      <MobileShell sesion={sesion} onCerrar={onCerrar}>
        {contenido}
      </MobileShell>
    );
  }
  return (
    <DesktopShell
      sesion={sesion}
      onCerrar={onCerrar}
      onBuscar={(q) => irA(`/activos?q=${encodeURIComponent(q)}`)}
    >
      {contenido}
    </DesktopShell>
  );
}

function Rutas(props: {
  readonly ruta: string;
  readonly sesion: SesionMe;
  readonly device: 'movil' | 'escritorio';
}): React.JSX.Element {
  const camino = pathDe(props.ruta);
  const ficha = /^\/activos\/([0-9a-f-]{36})$/i.exec(camino);
  if (ficha?.[1]) {
    return (
      <AssetDetailScreen
        sesion={props.sesion}
        id={ficha[1]}
        fromScan={consultaDe(props.ruta).get('from') === 'scan'}
      />
    );
  }
  const editar = /^\/activos\/([0-9a-f-]{36})\/editar$/i.exec(camino);
  if (editar?.[1]) {
    return <AssetFormScreen sesion={props.sesion} pasos={false} activoId={editar[1]} />;
  }
  if (camino.startsWith('/activos/nuevo')) {
    return <AssetFormScreen sesion={props.sesion} pasos={props.device === 'movil'} />;
  }
  if (camino.startsWith('/activos')) return <AssetsScreen sesion={props.sesion} />;
  if (camino.startsWith('/escanear')) return <ScannerScreen sesion={props.sesion} />;
  if (camino.startsWith('/operaciones')) return <ScanSessionScreen sesion={props.sesion} />;
  if (camino.startsWith('/alquileres')) return <RentalsScreen sesion={props.sesion} />;
  if (camino.startsWith('/mantenimiento')) return <MaintenanceScreen sesion={props.sesion} />;
  if (camino.startsWith('/preferencias')) return <PreferenciasScreen />;
  if (camino.startsWith('/reportes')) {
    return <ReportsScreen sesion={props.sesion} />;
  }
  if (camino.startsWith('/auditoria')) return <AuditScreen sesion={props.sesion} />;
  if (camino.startsWith('/importar')) {
    return props.device === 'escritorio' ? <ImportScreen sesion={props.sesion} /> : <AvisoSoloEscritorio />;
  }
  if (camino === '/' || camino.startsWith('/tablero')) return <DashboardScreen sesion={props.sesion} />;
  if (camino.startsWith('/avisos')) return <NotificationsScreen sesion={props.sesion} />;
  if (camino.startsWith('/marca')) {
    return props.device === 'escritorio' ? <BrandingScreen sesion={props.sesion} /> : <AvisoSoloEscritorio />;
  }
  if (camino.startsWith('/almacenamiento')) {
    return props.device === 'escritorio' ? <StorageScreen sesion={props.sesion} /> : <AvisoSoloEscritorio />;
  }
  if (camino.startsWith('/qr')) {
    return <QrBatchesDesktop sesion={props.sesion} />;
  }
  if (camino.startsWith('/maestros')) {
    return props.device === 'escritorio' ? <MastersDesktop sesion={props.sesion} /> : <AvisoSoloEscritorio />;
  }
  if (camino.startsWith('/plataforma')) {
    return props.device === 'escritorio' ? <PlatformScreen sesion={props.sesion} /> : <AvisoSoloEscritorio />;
  }
  return <DashboardScreen sesion={props.sesion} />;
}

function SelectorEmpresas(props: {
  readonly empresas: readonly EmpresaDeSesion[];
  readonly csrfToken: string;
  readonly onElegida: () => void;
  readonly onVolverAlPanel: (() => void) | null;
}): React.JSX.Element {
  return (
    <section className="flex w-full max-w-md flex-col gap-3">
      <p className="text-center text-brand-text">{textos.sesion.sinEmpresa}</p>
      <ul className="flex flex-col gap-2">
        {props.empresas.map((empresa) => (
          <li key={empresa.id}>
            <button
              type="button"
              className="flex min-h-tactil w-full items-center justify-between rounded-brand border border-brand-text-muted/20 bg-brand-surface px-4 text-left text-brand-text"
              onClick={() => {
                void elegirEmpresa(empresa.id, props.csrfToken).then(props.onElegida);
              }}
            >
              <span>{empresa.name}</span>
              <span className="text-sm text-brand-text-muted">{textos.acciones.usarEmpresa}</span>
            </button>
          </li>
        ))}
      </ul>
      {props.onVolverAlPanel ? (
        <Btn variante="secundario" onClick={props.onVolverAlPanel}>
          {textos.sesion.irAlPanel}
        </Btn>
      ) : null}
    </section>
  );
}
